import AddIcon from "@mui/icons-material/Add";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DateTime } from "luxon";
import { type FC, Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { RecurringShiftComponentSource } from "../../../../components/RecurringShift/RecurringShift";
import { RecurringShiftPanelWrapper } from "../../../../components/RecurringShift/RecurringShiftPanelWrapper";
import { MetaChip } from "../../../../components/UIToolkit/Badges/MetaChip";
import { EmptyState } from "../../../../components/UIToolkit/molecules/EmptyState";
import { PanelHeader } from "../../../../components/UIToolkit/molecules/PanelHeader";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../../contexts/ChosenDepartment";
import { ACCENT_BLUE, BADGE, CONTROL_HEIGHT, FONT_SIZE, GRID_RADIUS, INK, NEUTRAL, SHADOW, SURFACE } from "../../../../theme/tokens";
import type { HireGenerationChange, HireSettings } from "../hooks/useSuggestedHireEdits";
import type { GridDates } from "../types";
import { type GapTone, gapToneStyle } from "../utils/gapTones";
import { dayDateOf, formatGridDayDate } from "../utils/gridDates";
import { applyRecurringPanelClose, hirePrefillWeeks, prefillSummary } from "../utils/recurringPrefill";
import { dayIdxOf, displayNames, retiredShiftsOf, type ShiftLength, type SuggestedHireDraft, weekIdxOf } from "../utils/suggestedHires";
import { DAY_KEYS } from "../utils/weekStart";
import HireSettingsPopover from "./HireSettingsPopover";
import SuggestedHireRow from "./SuggestedHireRow";

type Props = {
	/** Already filtered to the shift types Settings' SHIFTS group has left in scope. */
	hires: SuggestedHireDraft[];
	settings: HireSettings;
	gridWeeks: number;
	weekNumbers: number[];
	weekStartDay: number;
	/** REQUIRED: threaded down to every row, so a missing wiring fails the build instead of the label. */
	dates: GridDates;
	shiftLabels: string[];
	/** The shift types in scope. Settings' SHIFTS group is THE page filter — it scopes the grid too. */
	enabledShiftLabels: string[];
	/** The shift lengths this department's templates run — the SHIFT LENGTH switch's only options. */
	shiftLengthOptions: ShiftLength[];
	/** The job these people would be hired into — pre-selected in the Recurring Shift modal. */
	jobId: string;
	/** Any stored decision exists — enables Revert. */
	hasEdits: boolean;
	markerToneFor: (hire: SuggestedHireDraft, key: number) => GapTone;
	selectedHireId: string | null;
	onSelectHire: (id: string | null) => void;
	onHoverHire: (id: string | null) => void;
	onRename: (id: string, name: string) => void;
	onShiftsChange: (id: string, labels: string[]) => void;
	onDayChange: (id: string, key: number, label: string | null) => void;
	onApplyToggle: (hire: SuggestedHireDraft) => void;
	onRemove: (id: string) => void;
	onAddHire: () => void;
	onReset: () => void;
	/** One typed shape for all three declarations of this callback — see `HireGenerationChange`. */
	onGenerationChange: (next: HireGenerationChange) => void;
	onToggleShift: (label: string) => void;
	/**
	 * A recurring shift was actually created or updated in the panel this row opens. The gaps grid,
	 * the recommendations and the suggestions all read the schedule template that just changed, so the
	 * page refetches them; a plain cancel never calls this.
	 */
	onRecurringShiftSaved: (hireId: string) => void;
};

/**
 * The legend chips shrink with the header's compact buttons rather than towering over them, and they
 * are RECTANGULAR (a small radius, not a pill): a pill reads as something you can tap or dismiss, and
 * these are a key — they only ever say what a colour means. Bold, because the label IS the tier name
 * and it sits in tinted ink that the fill deliberately keeps quiet.
 *
 * Roomier box (px 1 / py 0.5) around SMALLER type (10.5px), which is the trade a key wants: the
 * rectangle has to be big enough to read as a swatch of the tier's fill — that fill is the whole
 * message — while the words on it stay quieter than the panel title and the buttons beside them.
 */
const LEGEND_CHIP_SX = { minHeight: 26, px: 1, py: 0.5, borderRadius: GRID_RADIUS.CHIP, fontSize: FONT_SIZE.LABEL_XXS, fontWeight: 700 } as const;

/**
 * Legend swatch — the tier's SATURATED marker, the same field the day-chip corner markers read.
 *
 * Not the ink: ink is the accessible dark amber/red that has to sit under text (see `gapToneStyle`),
 * and using it here is what made the "yellow" tier's swatch read brown. A dot carries no text, so it
 * gets to be the colour the tier is named after — and it comes from `gapToneStyle` rather than from a
 * hex here, so the swatch and the markers it explains cannot drift apart.
 */
const LegendDot: FC<{ tone: GapTone }> = ({ tone }) => (
	<Box
		component="span"
		sx={{
			// `DOT_SIZE_XS`, the smallest step: inside a chip whose own fill is already the tier, 8px read
			// as the chip's subject and 6px still read as a bullet the label hung off. At 5px it reads as
			// a sample. The hairline below is what keeps it legible — and it matters MORE at this size,
			// because the fill it sits on has been lightened again (see `GAP_TONE`).
			width: BADGE.DOT_SIZE_XS,
			height: BADGE.DOT_SIZE_XS,
			flexShrink: 0,
			borderRadius: "50%",
			backgroundColor: gapToneStyle(tone).marker,
			// The tier's dark ink as a hairline, matching the ring on the day-chip markers. The dot sits ON
			// the tier's own pale fill, where the saturated yellow marker measures 1.55:1 — bright, and
			// edgeless without this. The ring itself is 2.08:1 against that fill, which is what draws the
			// edge. An outer ring, not a border, so the swatch's core stays exactly `DOT_SIZE_XS` wide.
			boxShadow: `0 0 0 1px ${alpha(gapToneStyle(tone).ink, 0.5)}`,
		}}
	/>
);

/** Fill / ink / edge for a legend chip — the tier's own surface tokens, so the key matches the grid. */
const legendChipTone = (tone: Exclude<GapTone, "neutral">) => {
	const style = gapToneStyle(tone);
	// DEFAULT: `bg` is optional on `GapToneStyle` because `neutral` has none, but the legend only ever
	// keys the two GRADED tiers, both of which do — the branch is unreachable and exists for the type.
	return { ink: style.ink, bg: style.bg ?? "transparent", border: style.bar };
};

/**
 * The shift picker's row: two choices and an escape, all reading as one line of controls.
 *
 * `MICRO` is the documented height for an action sitting INSIDE content, which this strip is — it
 * hangs under the row it belongs to rather than being page chrome.
 */
const PICKER_CHIP_SX = { minHeight: CONTROL_HEIGHT.MICRO, px: 1, fontSize: FONT_SIZE.LABEL_XS } as const;
const PICKER_CANCEL_SX = { minHeight: CONTROL_HEIGHT.MICRO, height: CONTROL_HEIGHT.MICRO, px: 1, fontSize: FONT_SIZE.LABEL_XS } as const;

/** The pressed state for the shift this day already works — the module's fixed interactive accent. */
const PICKER_CHIP_PRESSED_TONE = { ink: ACCENT_BLUE.INK, bg: ACCENT_BLUE.TINT, border: ACCENT_BLUE.MAIN };

/**
 * The recurring gap, packed into people you can edit and apply.
 *
 * The panel is presentation + local interaction only: every edit goes up to the localStorage layer, and
 * Apply is a client-side overlay on the gaps table — nothing here writes to the schedule template.
 *
 * Its header is a `PanelHeader` rather than a page-level title: the count lives in the compact summary
 * strip beside the tabs, and the substance of the panel is the rows, so the header only has to name the
 * section and hold the legend / revert / settings cluster.
 */
const SuggestedHiresPanel: FC<Props> = ({
	hires,
	settings,
	gridWeeks,
	weekNumbers,
	weekStartDay,
	dates,
	shiftLabels,
	enabledShiftLabels,
	shiftLengthOptions,
	jobId,
	hasEdits,
	markerToneFor,
	selectedHireId,
	onSelectHire,
	onHoverHire,
	onRename,
	onShiftsChange,
	onDayChange,
	onApplyToggle,
	onRemove,
	onAddHire,
	onReset,
	onGenerationChange,
	onToggleShift,
	onRecurringShiftSaved,
}) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { chosenDepartment } = useChosenDepartment();
	/**
	 * Names, computed for the WHOLE list rather than per row: the numbering ("Weekend", "Weekend 2") is a
	 * property of the list, not of one person, so a row cannot work it out alone. A row the user named
	 * keeps that name verbatim — see `displayNames`.
	 */
	const names = displayNames(hires, gridWeeks, settings.shiftsPerWeek, t);
	// Which day is waiting on a shift choice — only reachable once a person covers several shift types.
	const [pendingDay, setPendingDay] = useState<{ hireId: string; key: number } | null>(null);
	// The hire whose proposed week is open in the Recurring Shift modal, or null when it's closed.
	const [recurringHire, setRecurringHire] = useState<SuggestedHireDraft | null>(null);

	const pendingDayName = (key: number): string => {
		const dayIdx = dayIdxOf(key);
		const weekIdx = weekIdxOf(key);
		return t("hiring.hire.row.dayToggle", {
			day: t(`hiring.day.${DAY_KEYS[dayIdx]}`),
			date: formatGridDayDate(dayDateOf(dates, weekIdx, dayIdx, weekStartDay)),
			week: weekNumbers[weekIdx],
		});
	};

	const handleAddRecurring = (hire: SuggestedHireDraft) => {
		setRecurringHire(hire);
		const summary = prefillSummary(hire);
		track("hiring_suggested_hire_recurring_opened", {
			job_id: jobId,
			hire_id: hire.id,
			shifts: summary.shifts,
			days: summary.days,
			weeks: summary.weeks,
		});
	};

	// The hire is read BEFORE `setRecurringHire(null)` clears it — `onSaved` fires in the same close, and
	// the caller needs to know which draft just became a real shift so it can stop overlaying the grid.
	const handleRecurringClose = (didUpdateOrCreate?: boolean) => {
		const savedHireId = recurringHire?.id;
		applyRecurringPanelClose({
			didUpdateOrCreate,
			onClosed: () => setRecurringHire(null),
			onSaved: () => savedHireId !== undefined && onRecurringShiftSaved(savedHireId),
		});
	};

	return (
		<Box
			data-testid="hiring-suggested-hires"
			sx={{
				mb: 4,
				borderRadius: 2,
				backgroundColor: SURFACE,
				border: `1px solid ${NEUTRAL[200]}`,
				boxShadow: SHADOW.PANEL_SOFT,
				overflow: "hidden",
			}}
		>
			<PanelHeader
				icon={<GroupAddOutlinedIcon />}
				title={t("hiring.hire.title")}
				subtitle={t("hiring.hire.subtitle")}
				sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${NEUTRAL[150]}` }}
				endingContent={
					<Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
						<MetaChip
							label={t("hiring.hire.legend.short")}
							icon={<LegendDot tone="short" />}
							customTone={legendChipTone("short")}
							sx={LEGEND_CHIP_SX}
							dataTestId="hiring-hire-legend-short"
						/>
						<MetaChip
							label={t("hiring.hire.legend.critical")}
							icon={<LegendDot tone="critical" />}
							customTone={legendChipTone("critical")}
							sx={LEGEND_CHIP_SX}
							dataTestId="hiring-hire-legend-critical"
						/>
						<SystemButton
							buttonVariant="secondary"
							size="compact"
							sx={{ minHeight: 28, px: 1, fontSize: FONT_SIZE.LABEL_XS }}
							noFullWidthMobile
							disabled={!hasEdits}
							leadingIcon={<RotateLeftIcon sx={{ fontSize: 14 }} />}
							leadingIconGap="6px"
							onClick={onReset}
							tooltip={t("hiring.hire.revertTooltip")}
							data-testid="hiring-hire-revert"
						>
							{t("hiring.hire.revert")}
						</SystemButton>
						<HireSettingsPopover
							settings={settings}
							shiftLabels={shiftLabels}
							enabledShiftLabels={enabledShiftLabels}
							shiftLengthOptions={shiftLengthOptions}
							onToggleShift={onToggleShift}
							onGenerationChange={onGenerationChange}
						/>
					</Box>
				}
			/>

			{hires.length === 0 ? (
				<EmptyState
					icon={<TaskAltOutlinedIcon />}
					iconColor="success.dark"
					title={t("hiring.hire.empty.title")}
					hint={t("hiring.hire.empty.hint")}
					dataTestId="hiring-hire-empty"
					action={
						<SystemButton
							buttonVariant="secondary"
							size="compact"
							noFullWidthMobile
							leadingIcon={<AddIcon sx={{ fontSize: 14 }} />}
							onClick={onAddHire}
							data-testid="hiring-hire-add-empty"
						>
							{t("hiring.hire.addManually")}
						</SystemButton>
					}
				/>
			) : (
				// No height cap and no inner scroll: the list is the substance of the panel, and capping it
				// put a scrollbar OVER the rows and hid most of a 17-person set behind a gesture. The panel
				// grows to fit every hire and the page scrolls, which is the scroll the user already has.
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1, px: 1.5, pb: 1.5 }} data-testid="hiring-hire-rows">
					{hires.map((hire, index) => (
						<Fragment key={hire.id}>
							<SuggestedHireRow
								hire={hire}
								displayName={names[index]}
								gridWeeks={gridWeeks}
								weekNumbers={weekNumbers}
								weekStartDay={weekStartDay}
								dates={dates}
								shiftLabels={shiftLabels}
								markerToneFor={(key) => markerToneFor(hire, key)}
								selected={selectedHireId === hire.id}
								index={index}
								onSelectToggle={() => onSelectHire(selectedHireId === hire.id ? null : hire.id)}
								onHoverChange={(hovered) => onHoverHire(hovered ? hire.id : null)}
								onRename={(name) => onRename(hire.id, name)}
								onShiftsChange={(labels) => onShiftsChange(hire.id, labels)}
								onDayChange={(key, label) => onDayChange(hire.id, key, label)}
								onDayNeedsShift={(key) => setPendingDay({ hireId: hire.id, key })}
								pendingDayKey={pendingDay?.hireId === hire.id ? pendingDay.key : null}
								retiredShifts={retiredShiftsOf(hire, shiftLabels)}
								onApplyToggle={() => onApplyToggle(hire)}
								onRemove={() => onRemove(hire.id)}
								onAddRecurring={() => handleAddRecurring(hire)}
							/>
							{pendingDay?.hireId === hire.id && (
								<Box
									data-testid={`hiring-hire-day-picker-${hire.id}`}
									sx={{
										display: "flex",
										alignItems: "center",
										flexWrap: "wrap",
										gap: 1,
										px: 2.5,
										py: 1,
										backgroundColor: NEUTRAL[50],
										borderBottom: `1px solid ${NEUTRAL[150]}`,
									}}
								>
									<Typography sx={{ fontSize: FONT_SIZE.CAPTION, fontWeight: 600, color: INK.MUTED }}>
										{t("hiring.hire.row.pickShift", { day: pendingDayName(pendingDay.key) })}
									</Typography>
									{hire.coverShifts.map((label) => (
										<MetaChip
											key={label}
											label={label}
											tone="primary"
											// The shift this day already holds renders PRESSED. The picker opens on an OFF
											// day, so normally none is — but it also reopens on a day that is already on,
											// and there the two chips used to look identical with no sign which one the day
											// was actually working.
											customTone={hire.shiftByDay[pendingDay.key] === label ? PICKER_CHIP_PRESSED_TONE : undefined}
											sx={PICKER_CHIP_SX}
											onClick={() => {
												onDayChange(hire.id, pendingDay.key, label);
												setPendingDay(null);
											}}
											dataTestId={`hiring-hire-day-picker-${hire.id}-${label}`}
										/>
									))}
									<SystemButton
										buttonVariant="borderless"
										size="small"
										noFullWidthMobile
										// Sized to the shift chips it stands beside, not to the page's small button.
										// `size="small"` is still ~32px tall against their 24, so Cancel out-weighed the
										// two choices it is an escape from and set the whole strip's height.
										sx={PICKER_CANCEL_SX}
										onClick={() => setPendingDay(null)}
										data-testid={`hiring-hire-day-picker-cancel-${hire.id}`}
									>
										{t("hiring.hire.row.pickShiftCancel")}
									</SystemButton>
								</Box>
							)}
						</Fragment>
					))}
				</Box>
			)}

			{hires.length > 0 && (
				// A single left-aligned action, so no `space-between`: the footer's only job now is to
				// offer "add one more" under the list.
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						flexWrap: "wrap",
						gap: 1,
						px: 2,
						py: 1.25,
						borderTop: `1px solid ${NEUTRAL[150]}`,
					}}
				>
					<SystemButton
						buttonVariant="borderless"
						size="compact"
						noFullWidthMobile
						leadingIcon={<GroupAddOutlinedIcon sx={{ fontSize: 14 }} />}
						onClick={onAddHire}
						data-testid="hiring-hire-add"
					>
						{t("hiring.hire.addManually")}
					</SystemButton>
				</Box>
			)}

			{recurringHire !== null && (
				// The app's own Recurring Shift panel, opened pre-filled — the module proposes a schedule,
				// the schedule template still owns creating one. `forceNewPanel` because the v2 panel is the
				// one that lays the template out as `Week 1` / `Week 2` columns, which is the only shape a
				// hire whose weeks differ can be pre-filled into (see `hirePrefillWeeks`).
				//
				// Search Staff stays deliberately EMPTY: a suggested hire is a proposed headcount, not a
				// person, and the panel's Save is gated on picking one — which is the intended "I hired
				// someone, put this proposed schedule on them" flow. The prefill survives that choice.
				<RecurringShiftPanelWrapper
					isSynergyMS={false}
					renderWithoutModal={false}
					forceNewPanel
					shouldAddNewShiftOnOpen={false}
					source={RecurringShiftComponentSource.Other}
					// No clicked cell to anchor to — this opens from a list, not from a date. Today is the
					// honest anchor: it fixes which template week is "this" week, and each prefilled row walks
					// forward from it to its own week.
					// The DEPARTMENT's today, not the browser's. The panel derives its week start from the
					// department while this anchor was browser-local, so a manager working a facility across a
					// date boundary (09:00 in Israel is the previous day in California) could get the two from
					// different dates and prefill the wrong week of a multi-week template.
					clickedDate={DateTime.now().setZone(chosenDepartment.timezone)}
					prefilledWeeks={hirePrefillWeeks(recurringHire, jobId)}
					// A suggested hire is headcount for ONE job, so only the people who can work it are offered.
					staffJobIdFilter={jobId}
					onClose={handleRecurringClose}
				/>
			)}
		</Box>
	);
};

export default SuggestedHiresPanel;
